import React, { useEffect } from 'react';
import { Form, InputNumber, Switch, Select, Typography, Card, Row, Col, Tooltip, Space } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { Option } = Select;

const { Title, Text } = Typography;

/**
 * 产品特性可视化编辑组件
 * 根据产品类型显示不同的配额字段
 *
 * 新结构:
 * - subscription_limits: 订阅级别限制（资源数量限制）
 * - resource_features: 资源级别特性（每个资源的配置）
 * - periodic_usage: 周期性使用量（需要定期重置的配额）
 */
const ProductFeaturesEditor = ({ productTypeCode, form }) => {
    // 云盘：附加包（席位包/流量包）只配 resource_features；主套餐配 features + 月流量
    const isAddon = Form.useWatch('is_addon', form);
    // 重置周期选项
    const resetCycleOptions = [
        { value: 'daily', label: '每日' },
        { value: 'weekly', label: '每周' },
        { value: 'monthly', label: '每月' },
        { value: 'quarterly', label: '每季度' },
        { value: 'yearly', label: '每年' }
    ];

    // DNS 产品特性配置
    // DNS是按zone订阅的，没有账户级别限制，所有特性都是zone级别的
    const dnsFeatures = {
        subscription: [], // DNS没有账户级别限制
        periodic: [
            {
                key: 'dns_queries',
                label: 'DNS查询数配额',
                tooltip: '每周期可使用的DNS查询次数，-1为无限制',
                min: -1,
                max: 100000000000,
                defaultValue: 100000,
                resetCycle: 'monthly'
            }
        ],
        resource: [
            {
                key: 'dns_records_per_zone_limit',
                label: '每区域记录数限制',
                tooltip: '单个DNS区域最多可添加的记录数，-1为无限制',
                min: -1,
                max: 100000,
                defaultValue: 50
            },
            {
                key: 'dns_cidr_per_zone_limit',
                label: 'CIDR数量限制',
                tooltip: '可配置的CIDR地理规则数量（账户所有DNS套餐额度总和），-1为无限制',
                min: -1,
                max: 10000,
                defaultValue: 5
            },
            {
                key: 'dns_qps_limit',
                label: 'DNS查询QPS限制',
                tooltip: '每秒查询率限制(Queries Per Second)，-1为无限制',
                min: -1,
                max: 1000000,
                defaultValue: 1000
            },
            {
                key: 'dns_qos_level',
                label: 'QoS服务等级',
                tooltip: '服务质量等级(1-5)',
                min: 1,
                max: 5,
                defaultValue: 1
            },
            {
                key: 'dns_resolution_precision',
                label: '解析精度',
                tooltip: '地理位置解析精度',
                type: 'select',
                options: [
                    { value: 'country', label: '国家级' },
                    { value: 'province', label: '省级' },
                    { value: 'city', label: '城市级' },
                    { value: 'isp', label: 'ISP级' }
                ],
                defaultValue: 'country'
            },
            {
                key: 'dns_dnssec',
                label: 'DNSSEC支持',
                tooltip: '是否支持DNS安全扩展',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'dns_anycast',
                label: 'Anycast网络',
                tooltip: '是否启用Anycast路由',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'dns_qos_editable',
                label: 'QoS可编辑',
                tooltip: '用户是否可自定义QoS配置',
                type: 'boolean',
                defaultValue: false
            }
        ]
    };

    // CDN 产品特性配置
    const cdnFeatures = {
        // 写入 products.features(执行源:日志查询按保留天数截断、缓存设置按级别限制)
        plan: [
            {
                key: 'cdn_cache_level',
                label: '缓存级别',
                tooltip: '基础:默认缓存/URL与整站清除/浏览器缓存;高级:+缓存键、自定义TTL规则、页面规则缓存、过期缓存;全功能:+缓存所有内容、通配清除、分层加速',
                type: 'select',
                optional: true,
                options: [
                    { value: 'basic', label: '基础 (basic)' },
                    { value: 'advanced', label: '高级 (advanced)' },
                    { value: 'full', label: '全功能 (full)' }
                ]
            },
            {
                key: 'cdn_log_retention',
                label: '日志保留',
                tooltip: '用户可查询的访问日志/统计天数,留空 = 7 天',
                type: 'days',
                optional: true
            }
        ],
        periodic: [
            {
                key: 'traffic_bytes',
                label: '流量配额(字节)',
                tooltip: '每周期可使用的流量(字节)，-1为无限制',
                min: -1,
                max: 100000000000000,
                defaultValue: 10737418240,
                resetCycle: 'monthly'
            },
            {
                key: 'requests',
                label: '请求数配额',
                tooltip: '每周期可使用的请求次数，-1为无限制',
                min: -1,
                max: 100000000000,
                defaultValue: 1000000,
                resetCycle: 'monthly'
            }
        ],
        resource: [
            {
                key: 'cdn_hostnames_limit',
                label: '每域名主机名数限制',
                tooltip: '单个CDN域名可配置的回源主机名数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 5
            },
            {
                key: 'cdn_rules_limit',
                label: '规则数量限制',
                tooltip: '每个域名可添加的规则数量，-1为无限制',
                min: -1,
                max: 1000,
                defaultValue: 10
            },
            {
                key: 'cdn_worker_routes_limit',
                label: 'Worker路由数限制',
                tooltip: '每个域名可添加的Worker路由数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 5
            },
            {
                key: 'cdn_purge_limit_per_day',
                label: '每日刷新次数限制',
                tooltip: '每天可手动刷新缓存的次数，-1为无限制',
                min: -1,
                max: 10000,
                defaultValue: 10
            },
            {
                key: 'cdn_waf_enabled',
                label: 'WAF防护',
                tooltip: '是否启用Web应用防火墙',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'cdn_custom_ssl',
                label: '自定义SSL证书',
                tooltip: '是否支持上传自定义SSL证书',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'cdn_websocket_enabled',
                label: 'WebSocket支持',
                tooltip: '是否支持WebSocket协议加速',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'cdn_ws_max_conns',
                label: 'WS 同时在线连接',
                tooltip: '每域名每节点同时在线的 WebSocket 连接上限，0 为不限',
                min: 0,
                max: 1000000,
                defaultValue: 0,
                optional: true
            },
            {
                key: 'cdn_ws_max_secs',
                label: 'WS 单连接最长(秒)',
                tooltip: '到时断开，客户端重连即可，0 为不限',
                min: 0,
                max: 604800,
                defaultValue: 0,
                optional: true
            },
            {
                key: 'cdn_ws_rate_kbps',
                label: 'WS 单连接速率(kbps)',
                tooltip: '每条连接每个方向的速率上限，1000 = 1 Mbps，0 为不限',
                min: 0,
                max: 10000000,
                defaultValue: 0,
                optional: true
            },
            {
                key: 'cdn_ws_new_per_ip_min',
                label: 'WS 每 IP 每分钟新建',
                tooltip: '每个访客 IP 每分钟新建 WebSocket 连接上限，0 为不限',
                min: 0,
                max: 100000,
                defaultValue: 0,
                optional: true
            },
            {
                key: 'cdn_ws_block_proxy',
                label: 'WS 拦截代理隧道',
                tooltip: '识别到 Trojan / VLESS / Xray 等代理协议即断开(不开只记录)',
                type: 'boolean',
                defaultValue: false,
                optional: true
            }
        ]
    };

    // 域名注册产品特性配置
    const domainFeatures = {
        subscription: [
            {
                key: 'domain_registrations_limit',
                label: '域名注册数量限制',
                tooltip: '用户可注册和管理的域名总数，-1为无限制',
                min: -1,
                max: 10000,
                defaultValue: 10
            }
        ],
        periodic: [],
        resource: [
            {
                key: 'domain_auto_renew_enabled',
                label: '自动续费',
                tooltip: '是否支持域名自动续费功能',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'domain_privacy_protection_enabled',
                label: '隐私保护',
                tooltip: '是否提供WHOIS隐私保护',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'domain_transfer_lock_enabled',
                label: '转移锁',
                tooltip: '是否支持域名转移锁定功能',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'domain_dns_management_enabled',
                label: 'DNS管理',
                tooltip: '是否提供DNS管理功能',
                type: 'boolean',
                defaultValue: true
            }
        ]
    };

    // TM 事务性邮件产品特性配置
    const tmFeatures = {
        subscription: [
            {
                key: 'tm_mail_servers_limit',
                label: '邮件服务器数量',
                tooltip: '可创建的邮件服务器(Organization)数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 1
            }
        ],
        periodic: [
            {
                key: 'tm_daily_send_limit',
                label: '每日发送限制',
                tooltip: '每日可发送的邮件数量',
                min: 0,
                max: 10000000,
                defaultValue: 1000,
                resetCycle: 'daily'
            },
            {
                key: 'tm_monthly_send_limit',
                label: '每月发送限制',
                tooltip: '每月可发送的邮件总数',
                min: 0,
                max: 100000000,
                defaultValue: 30000,
                resetCycle: 'monthly'
            }
        ],
        resource: [
            {
                key: 'tm_domains_per_server',
                label: '每服务器域名数',
                tooltip: '每个邮件服务器可添加的域名数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 5
            },
            {
                key: 'tm_credentials_per_server',
                label: '每服务器凭证数',
                tooltip: '每个邮件服务器可创建的API/SMTP凭证数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 10
            },
            {
                key: 'tm_message_retention_days',
                label: '消息保留天数',
                tooltip: '消息记录保留时间',
                min: 1,
                max: 365,
                defaultValue: 7
            },
            {
                key: 'tm_smtp_enabled',
                label: 'SMTP发送',
                tooltip: '是否允许通过SMTP发送邮件',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'tm_api_enabled',
                label: 'API发送',
                tooltip: '是否允许通过API发送邮件',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'tm_webhooks_enabled',
                label: 'Webhook通知',
                tooltip: '是否启用邮件状态Webhook回调',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'tm_tracking_enabled',
                label: '打开/点击跟踪',
                tooltip: '是否启用邮件打开和链接点击跟踪',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'tm_dedicated_ip',
                label: '专用IP',
                tooltip: '是否分配专用发送IP',
                type: 'boolean',
                defaultValue: false
            }
        ]
    };

    // Host 虚拟主机产品特性配置
    const hostFeatures = {
        subscription: [
            {
                key: 'host_sites_limit',
                label: '域名绑定数量',
                tooltip: '容器可绑定的域名数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 5
            },
            {
                key: 'host_ssl_certificates_limit',
                label: 'SSL证书数量',
                tooltip: '可上传的SSL证书数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 10
            },
            {
                key: 'host_backup_count',
                label: '备份数量',
                tooltip: '保留的备份数量，0表示不备份，-1为无限制',
                min: -1,
                max: 30,
                defaultValue: 0
            },
            {
                key: 'host_monitoring_enabled',
                label: '资源监控',
                tooltip: '是否启用资源监控功能',
                type: 'boolean',
                defaultValue: true
            }
        ],
        periodic: [
            {
                key: 'host_monthly_bandwidth_gb',
                label: '月流量配额(GB)',
                tooltip: '每月流量配额，超出后由计划任务监控和处理',
                min: 0,
                max: 100000,
                defaultValue: 500,
                resetCycle: 'monthly'
            }
        ],
        resource: [
            {
                key: 'container_cpu_limit',
                label: 'CPU限制',
                tooltip: 'CPU核心数限制',
                type: 'select',
                options: [
                    { value: '0.5', label: '0.5核' },
                    { value: '1', label: '1核' },
                    { value: '2', label: '2核' },
                    { value: '4', label: '4核' },
                    { value: '8', label: '8核' },
                    { value: '16', label: '16核' }
                ],
                defaultValue: '2'
            },
            {
                key: 'container_memory_limit_gb',
                label: '内存限制(GB)',
                tooltip: '容器内存限制',
                min: 0.5,
                max: 128,
                defaultValue: 2
            },
            {
                key: 'container_disk_limit_gb',
                label: '磁盘限制(GB)',
                tooltip: '容器磁盘空间限制',
                min: 1,
                max: 1000,
                defaultValue: 20
            },
            {
                key: 'container_max_connections',
                label: '最大连接数',
                tooltip: '并发连接数限制，-1为无限制',
                min: -1,
                max: 10000,
                defaultValue: 100
            },
            {
                key: 'php_versions',
                label: 'PHP版本',
                tooltip: '支持的PHP版本(可多选)',
                type: 'multiselect',
                options: [
                    { value: '8.1', label: 'PHP 8.1' },
                    { value: '8.2', label: 'PHP 8.2' },
                    { value: '8.3', label: 'PHP 8.3' }
                ],
                defaultValue: ['8.2', '8.3']
            },
            {
                key: 'python_versions',
                label: 'Python版本',
                tooltip: '支持的Python版本(可多选)',
                type: 'multiselect',
                options: [
                    { value: '3.10', label: 'Python 3.10' },
                    { value: '3.11', label: 'Python 3.11' },
                    { value: '3.12', label: 'Python 3.12' }
                ],
                defaultValue: ['3.11']
            },
            {
                key: 'node_versions',
                label: 'Node.js版本',
                tooltip: '支持的Node.js版本(可多选)',
                type: 'multiselect',
                options: [
                    { value: '18', label: 'Node.js 18' },
                    { value: '20', label: 'Node.js 20' },
                    { value: '22', label: 'Node.js 22' }
                ],
                defaultValue: ['20']
            },
            {
                key: 'version_switch_enabled',
                label: '版本热切换',
                tooltip: '是否支持运行时版本切换(无需重启容器)',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'process_management_enabled',
                label: '进程管理',
                tooltip: '是否允许管理进程(启动/停止/重启)',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'mysql_enabled',
                label: 'MySQL数据库',
                tooltip: '是否启用MySQL数据库服务',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'redis_enabled',
                label: 'Redis缓存',
                tooltip: '是否启用Redis缓存服务',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'postgresql_enabled',
                label: 'PostgreSQL',
                tooltip: '是否启用PostgreSQL数据库',
                type: 'boolean',
                defaultValue: false
            },
            {
                key: 'file_upload_max_size_mb',
                label: '文件上传限制(MB)',
                tooltip: '单文件上传大小限制',
                min: 1,
                max: 2048,
                defaultValue: 50
            }
        ]
    };

    // 共享主机 (EasyPanel) 产品特性配置
    const hostSharedFeatures = {
        subscription: [
            {
                key: 'host_vhosts_limit',
                label: '虚拟主机数量',
                tooltip: '该订阅可创建的虚拟主机数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 1
            }
        ],
        periodic: [
            {
                key: 'bandwidth_bytes',
                label: '月流量配额(字节)',
                tooltip: '每月流量配额(字节)，-1为无限制',
                min: -1,
                max: 100000000000000,
                defaultValue: 10737418240,
                resetCycle: 'monthly'
            }
        ],
        resource: [
            {
                key: 'disk_quota_mb',
                label: '磁盘空间(MB)',
                tooltip: '虚拟主机磁盘空间限制',
                min: 100,
                max: 102400,
                defaultValue: 1024
            },
            {
                key: 'db_quota_mb',
                label: '数据库空间(MB)',
                tooltip: 'MySQL数据库空间限制',
                min: 10,
                max: 10240,
                defaultValue: 100
            },
            {
                key: 'bandwidth_quota_gb',
                label: '月流量(GB)',
                tooltip: '每月流量配额(GB)',
                min: 1,
                max: 10000,
                defaultValue: 10
            },
            {
                key: 'ftp_quota',
                label: 'FTP账户数',
                tooltip: '可创建的FTP账户数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 1
            },
            {
                key: 'cron_quota',
                label: '计划任务数',
                tooltip: '可创建的定时任务数量，-1为无限制',
                min: -1,
                max: 100,
                defaultValue: 3
            },
            {
                key: 'domain_quota',
                label: '域名绑定数',
                tooltip: '可绑定的域名数量，-1为无限制',
                min: -1,
                max: 1000,
                defaultValue: 3
            },
            {
                key: 'php_enabled',
                label: 'PHP支持',
                tooltip: '是否支持PHP运行环境',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'ssl_enabled',
                label: 'SSL证书',
                tooltip: '是否支持SSL证书配置',
                type: 'boolean',
                defaultValue: true
            },
            {
                key: 'subdir_enabled',
                label: '子目录绑定',
                tooltip: '是否支持子目录绑定域名',
                type: 'boolean',
                defaultValue: true
            }
        ]
    };

    // 域名邮箱 (yeapmail)：读写 products.features（WebmailDomain::syncFeaturesFromSubscription 读取这些键）
    const yeapmailFeatures = {
        resourcePrefix: 'features',
        subscription: [],
        periodic: [],
        resource: [
            { key: 'mail_users_limit', label: '邮箱用户数', tooltip: '-1 为无限制', min: -1, max: 100000, defaultValue: 5 },
            { key: 'mail_aliases_limit', label: '邮件别名数', tooltip: '-1 为无限制', min: -1, max: 100000, defaultValue: 10 },
            { key: 'mail_alias_domains', label: '允许的别名域数量', tooltip: '一个邮箱域名可绑定的别名域数量，0 为不支持，-1 为无限制', min: -1, max: 1000, defaultValue: 1 },
            { key: 'mail_storage_gb', label: '存储空间 (GB)', tooltip: '域名总存储', min: 0, max: 100000, defaultValue: 5 },
            { key: 'mail_daily_send_limit', label: '每日发信上限', tooltip: '-1 为无限制', min: -1, max: 10000000, defaultValue: 100 },
            { key: 'mail_attachment_size_mb', label: '附件大小上限 (MB)', tooltip: '单个附件', min: 1, max: 1024, defaultValue: 25 },
        ],
    };

    // 云盘 (disk)：主套餐读 products.features（DiskQuotaService / DiskProvisioner 读这些键，见 docs/disk/SPEC.md §2）
    const diskFeatures = isAddon ? {
        resourcePrefix: 'resource_features',
        subscription: [],
        periodic: [],
        resource: [
            { key: 'disk_seats_5g', label: '5 GB 席位（每份）', tooltip: '每购买一份增加的 5 GB 席位数；流量包留空', min: 0, max: 10000, optional: true },
            { key: 'disk_seats_10g', label: '10 GB 席位（每份）', tooltip: '每购买一份增加的 10 GB 席位数；流量包留空', min: 0, max: 10000, optional: true },
        ],
    } : {
        resourcePrefix: 'features',
        subscription: [],
        periodic: [
            { key: 'disk_traffic_bytes', label: '月直链流量(字节)', tooltip: '直链 + 分享下载流量；1 GB = 1073741824，-1 为无限制', min: -1, max: 1e15, defaultValue: 107374182400, resetCycle: 'monthly' },
        ],
        resource: [
            {
                key: 'disk_mode', label: '云盘模式', tooltip: '个人 / 组织共享空间 / 组织按席位', type: 'select', defaultValue: 'personal',
                options: [{ value: 'personal', label: '个人' }, { value: 'org_pool', label: '组织 · 共享空间' }, { value: 'org_seat', label: '组织 · 按席位' }],
            },
            { key: 'disk_storage_gb', label: '存储空间 (GB)', tooltip: '个人空间或组织共享空间容量；按席位模式填 0', min: 0, max: 10000000, defaultValue: 100 },
            { key: 'disk_seats', label: '成员上限', tooltip: '共享空间模式的成员数，0 = 不限', min: 0, max: 100000, defaultValue: 0 },
            { key: 'disk_seats_5g', label: '自带 5 GB 席位', tooltip: '按席位模式套餐自带的席位数（另购走附加包）', min: 0, max: 100000, defaultValue: 0 },
            { key: 'disk_seats_10g', label: '自带 10 GB 席位', tooltip: '按席位模式套餐自带的席位数（另购走附加包）', min: 0, max: 100000, defaultValue: 0 },
        ],
    };

    // 根据产品类型选择特性配置
    let featuresConfig = null;
    if (productTypeCode === 'dns') {
        featuresConfig = dnsFeatures;
    } else if (productTypeCode === 'cdn') {
        featuresConfig = cdnFeatures;
    } else if (productTypeCode === 'domain') {
        featuresConfig = domainFeatures;
    } else if (productTypeCode === 'host_shared') {
        featuresConfig = hostSharedFeatures;
    } else if (productTypeCode === 'host') {
        featuresConfig = hostFeatures;
    } else if (productTypeCode === 'tm') {
        featuresConfig = tmFeatures;
    } else if (productTypeCode === 'yeapmail') {
        featuresConfig = yeapmailFeatures;
    } else if (productTypeCode === 'disk') {
        featuresConfig = diskFeatures;
    }

    // 当 productTypeCode 变化时，为未设置值的字段设置默认值
    useEffect(() => {
        if (!featuresConfig || !form) return;

        const defaults = {};

        // 订阅级别限制
        featuresConfig.subscription?.forEach(f => {
            const val = form.getFieldValue(['subscription_limits', f.key]);
            if (val === undefined || val === null) {
                if (!defaults.subscription_limits) defaults.subscription_limits = {};
                defaults.subscription_limits[f.key] = f.defaultValue;
            }
        });

        // 资源级别特性
        const resPrefix = featuresConfig.resourcePrefix || 'resource_features';
        featuresConfig.resource?.forEach(f => {
            const val = form.getFieldValue([resPrefix, f.key]);
            if ((val === undefined || val === null) && f.defaultValue !== undefined) {
                if (!defaults[resPrefix]) defaults[resPrefix] = {};
                defaults[resPrefix][f.key] = f.defaultValue;
            }
        });

        // 周期性使用量
        featuresConfig.periodic?.forEach(f => {
            const valObj = form.getFieldValue(['periodic_usage', f.key, 'value']);
            const cycleObj = form.getFieldValue(['periodic_usage', f.key, 'reset_cycle']);
            if (valObj === undefined || valObj === null) {
                if (!defaults.periodic_usage) defaults.periodic_usage = {};
                if (!defaults.periodic_usage[f.key]) defaults.periodic_usage[f.key] = {};
                defaults.periodic_usage[f.key].value = f.defaultValue;
            }
            if (cycleObj === undefined || cycleObj === null) {
                if (!defaults.periodic_usage) defaults.periodic_usage = {};
                if (!defaults.periodic_usage[f.key]) defaults.periodic_usage[f.key] = {};
                defaults.periodic_usage[f.key].reset_cycle = f.resetCycle || 'monthly';
            }
        });

        if (Object.keys(defaults).length > 0) {
            form.setFieldsValue(defaults);
        }
    }, [productTypeCode, featuresConfig, form]);

    if (!featuresConfig) {
        return (
            <Card>
                <Text type="secondary">请先选择产品类型</Text>
            </Card>
        );
    }

    // 渲染单个特性字段
    const renderFeatureField = (feature, prefix) => {
        const fieldName = [prefix, feature.key];

        if (feature.type === 'boolean') {
            return (
                <Form.Item
                    key={feature.key}
                    name={fieldName}
                    label={
                        <span>
                            {feature.label}{' '}
                            <Tooltip title={feature.tooltip}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                            </Tooltip>
                        </span>
                    }
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
            );
        }

        if (feature.type === 'select') {
            return (
                <Form.Item
                    key={feature.key}
                    name={fieldName}
                    label={
                        <span>
                            {feature.label}{' '}
                            <Tooltip title={feature.tooltip}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                            </Tooltip>
                        </span>
                    }
                    rules={feature.optional ? [] : [{ required: true, message: `请选择${feature.label}` }]}
                >
                    <Select style={{ width: '100%' }} allowClear={!!feature.optional} placeholder={feature.optional ? '未设置' : undefined}>
                        {feature.options.map(opt => (
                            <Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            );
        }

        if (feature.type === 'days') {
            // 存为 "Ndays"(如 30days),编辑为天数
            return (
                <Form.Item
                    key={feature.key}
                    name={fieldName}
                    label={
                        <span>
                            {feature.label}{' '}
                            <Tooltip title={feature.tooltip}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                            </Tooltip>
                        </span>
                    }
                    getValueProps={(v) => {
                        const m = String(v ?? '').match(/^\s*(\d+)/);
                        return { value: m ? Number(m[1]) : null };
                    }}
                    normalize={(n) => (n === null || n === undefined || n === '' ? undefined : `${n}days`)}
                >
                    <InputNumber min={1} max={3650} precision={0} addonAfter="天" style={{ width: '100%' }} placeholder="7" />
                </Form.Item>
            );
        }

        if (feature.type === 'multiselect') {
            return (
                <Form.Item
                    key={feature.key}
                    name={fieldName}
                    label={
                        <span>
                            {feature.label}{' '}
                            <Tooltip title={feature.tooltip}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                            </Tooltip>
                        </span>
                    }
                    rules={[{ required: true, message: `请选择${feature.label}` }]}
                >
                    <Select mode="multiple" style={{ width: '100%' }} placeholder={`选择${feature.label}`}>
                        {feature.options.map(opt => (
                            <Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            );
        }

        return (
            <Form.Item
                key={feature.key}
                name={fieldName}
                label={
                    <span>
                        {feature.label}{' '}
                        <Tooltip title={feature.tooltip}>
                            <QuestionCircleOutlined style={{ color: '#999' }} />
                        </Tooltip>
                    </span>
                }
                rules={feature.optional ? [] : [{ required: true, message: `请输入${feature.label}` }]}
            >
                <InputNumber
                    min={feature.min}
                    max={feature.max}
                    style={{ width: '100%' }}
                    placeholder={feature.defaultValue === undefined ? '可留空' : `默认: ${feature.defaultValue}`}
                />
            </Form.Item>
        );
    };

    // 渲染周期性配额字段（包含值和重置周期）
    const renderPeriodicFeatureField = (feature) => {
        return (
            <div key={feature.key} style={{ marginBottom: '24px' }}>
                <Form.Item
                    name={['periodic_usage', feature.key, 'value']}
                    label={
                        <span>
                            {feature.label}{' '}
                            <Tooltip title={feature.tooltip}>
                                <QuestionCircleOutlined style={{ color: '#999' }} />
                            </Tooltip>
                        </span>
                    }
                    rules={[{ required: true, message: `请输入${feature.label}` }]}
                    style={{ marginBottom: '8px' }}
                >
                    <InputNumber
                        min={feature.min}
                        max={feature.max}
                        style={{ width: '100%' }}
                        placeholder={`默认: ${feature.defaultValue}`}
                    />
                </Form.Item>
                <Form.Item
                    name={['periodic_usage', feature.key, 'reset_cycle']}
                    label="重置周期"
                    style={{ marginBottom: 8 }}
                >
                    <Select style={{ width: '100%' }}>
                        {resetCycleOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                {/* 超额策略：suspend=超限暂停（默认）；charge=超出按量计费（ElasticBillingService） */}
                <Form.Item name={['periodic_usage', feature.key, 'overage', 'mode']} label="超出后" style={{ marginBottom: 8 }} initialValue="suspend">
                    <Select style={{ width: '100%' }} options={[{ value: 'suspend', label: '暂停服务' }, { value: 'charge', label: '按量计费（周期末出超额账单）' }]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(a, b) => a?.periodic_usage?.[feature.key]?.overage?.mode !== b?.periodic_usage?.[feature.key]?.overage?.mode}>
                    {({ getFieldValue }) => getFieldValue(['periodic_usage', feature.key, 'overage', 'mode']) === 'charge' && (
                        <Space wrap style={{ width: '100%' }}>
                            <Form.Item name={['periodic_usage', feature.key, 'overage', 'unit']} label="计价单位" tooltip="字节类填 1073741824 = 1GB" style={{ marginBottom: 0 }}>
                                <InputNumber min={1} style={{ width: 160 }} />
                            </Form.Item>
                            <Form.Item name={['periodic_usage', feature.key, 'overage', 'unit_price']} label="单价" style={{ marginBottom: 0 }}>
                                <InputNumber min={0} step={0.01} style={{ width: 110 }} />
                            </Form.Item>
                            <Form.Item name={['periodic_usage', feature.key, 'overage', 'currency']} label="币种" initialValue="USD" style={{ marginBottom: 0 }}>
                                <Select style={{ width: 90 }} options={[{ value: 'USD', label: 'USD' }, { value: 'CNY', label: 'CNY' }]} />
                            </Form.Item>
                            <Form.Item name={['periodic_usage', feature.key, 'overage', 'max_amount']} label="安全上限" tooltip="单周期超额费用到此值仍暂停，0=不限" style={{ marginBottom: 0 }}>
                                <InputNumber min={0} style={{ width: 110 }} />
                            </Form.Item>
                        </Space>
                    )}
                </Form.Item>
            </div>
        );
    };

    return (
        <div>
            {featuresConfig.plan && featuresConfig.plan.length > 0 && (
                <Card
                    title={
                        <div>
                            <Title level={5} style={{ margin: 0 }}>套餐功能</Title>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                展示在套餐卡片并实际执行
                            </Text>
                        </div>
                    }
                    style={{ marginBottom: '16px' }}
                >
                    <Row gutter={16}>
                        {featuresConfig.plan.map(feature => (
                            <Col span={12} key={feature.key}>
                                {renderFeatureField(feature, 'features')}
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            {featuresConfig.subscription && featuresConfig.subscription.length > 0 && (
                <Card
                    title={
                        <div>
                            <Title level={5} style={{ margin: 0 }}>订阅级别限制</Title>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                限制订阅可创建的资源数量
                            </Text>
                        </div>
                    }
                    style={{ marginBottom: '16px' }}
                >
                    <Row gutter={16}>
                        {featuresConfig.subscription.map(feature => (
                            <Col span={12} key={feature.key}>
                                {renderFeatureField(feature, 'subscription_limits')}
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            {featuresConfig.periodic && featuresConfig.periodic.length > 0 && (
                <Card
                    title={
                        <div>
                            <Title level={5} style={{ margin: 0 }}>周期性使用量配额</Title>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                需要定期重置的使用量限制（如流量、请求数）
                            </Text>
                        </div>
                    }
                    style={{ marginBottom: '16px' }}
                >
                    <Row gutter={16}>
                        {featuresConfig.periodic.map(feature => (
                            <Col span={12} key={feature.key}>
                                {renderPeriodicFeatureField(feature)}
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            <Card
                title={
                    <div>
                        <Title level={5} style={{ margin: 0 }}>资源级别特性</Title>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            单个资源的规格和功能配置
                        </Text>
                    </div>
                }
            >
                <Row gutter={16}>
                    {featuresConfig.resource.map(feature => (
                        <Col span={12} key={feature.key}>
                            {renderFeatureField(feature, featuresConfig.resourcePrefix || 'resource_features')}
                        </Col>
                    ))}
                </Row>
            </Card>
        </div>
    );
};

export default ProductFeaturesEditor;
